<script setup>
/* global axios */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const brandLogoUrl = '/brand-assets/mi-marciana-logo.png';
const accountId = computed(() => route.params.accountId);
const baseUrl = computed(() => `/api/v1/accounts/${accountId.value}`);
const threads = ref([]);
const agents = ref([]);
const currentUserId = ref(null);
const activeThread = ref(null);
const messages = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const threadPage = ref(1);
const threadsHaveMore = ref(false);
const body = ref('');
const composeType = ref('message');
const assignedToId = ref('');
const dueAt = ref('');
const priority = ref('normal');
const linkedConversationId = ref(route.query.conversation_id || '');
const recentConversations = ref([]);
const showConversationPicker = ref(false);
const conversationSearch = ref('');
const showNewThread = ref(false);
const showNewTask = ref(false);
const newThreadName = ref('');
const newMemberIds = ref([]);
const newTaskBody = ref('');
const newTaskResponsibleId = ref('');
const newTaskParticipantIds = ref([]);
const newTaskDueAt = ref('');
const newTaskFollowUp = ref(false);
const creatingTask = ref(false);
const isFollowupMode = ref(String(route.query.task_view || '') === 'followups');
const taskView = ref(isFollowupMode.value ? 'today' : 'mine');
const messageList = ref(null);
const fileInput = ref(null);
const documentInput = ref(null);
const filtersNav = ref(null);
const showComposerMenu = ref(false);
const showCatalogSearch = ref(false);
const catalogSearchType = ref('product');
const catalogQuery = ref('');
const catalogResults = ref([]);
const catalogLoading = ref(false);
const catalogError = ref('');
const catalogImagePreview = ref(null);
const isRecording = ref(false);
const isSending = ref(false);
const quickReplyValue = ref('');
const sendError = ref('');
const mobileViewportStyle = ref({});
const validInboxFilters = new Set([
  'all',
  'comments',
  'automations',
  'scheduled',
  'team',
  'tasks',
  'urgent',
]);
const requestedInboxFilter = String(route.query.filter || 'all');
const activeInboxFilter = ref(
  validInboxFilters.has(requestedInboxFilter) ? requestedInboxFilter : 'all'
);
const inboxPanelWidth = ref(320);
const isResizingInbox = ref(false);
let mediaRecorder;
let audioChunks = [];
let catalogSearchTimer;
let resizeStartX = 0;
let resizeStartWidth = 320;

const isBotThread = (thread, botType = '') => {
  const descriptor = [thread.name, thread.messages?.[0]?.body]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('es');

  if (botType === 'comments') {
    return (
      descriptor.includes('bot comentarios') ||
      descriptor.includes('comentarios instagram')
    );
  }
  if (botType === 'automations') {
    return (
      descriptor.includes('bot automatizaciones') ||
      descriptor.includes('automatización') ||
      descriptor.includes('automatizacion')
    );
  }
  if (botType === 'scheduled') {
    return (
      descriptor.includes('bot programados') ||
      descriptor.includes('respuesta automática activada')
    );
  }
  return (
    isBotThread(thread, 'comments') ||
    isBotThread(thread, 'automations') ||
    isBotThread(thread, 'scheduled')
  );
};

const threadTask = thread =>
  thread.task || thread.messages?.find(message => message.kind === 'task');
const isTaskGroup = thread => /^Tarea ·(?:\s|$)/i.test(thread.name || '');
const isTaskThread = thread => isTaskGroup(thread);
const isUrgentThread = thread => {
  const message = thread.messages?.[0];
  return (
    message?.priority === 'urgent' ||
    /\burgente\b/i.test(`${thread.name || ''} ${message?.body || ''}`)
  );
};

const matchesInboxFilter = (thread, filter) => {
  if (filter === 'all') return !isBotThread(thread, 'comments');
  if (filter === 'comments') return isBotThread(thread, 'comments');
  if (filter === 'automations') return isBotThread(thread, 'automations');
  if (filter === 'scheduled') return isBotThread(thread, 'scheduled');
  if (filter === 'team') return !isBotThread(thread) && !isTaskThread(thread);
  if (filter === 'tasks') return isTaskThread(thread) && !isBotThread(thread);
  if (filter === 'urgent') return isUrgentThread(thread);
  return true;
};

const inboxFilters = computed(() =>
  [
    { key: 'all', label: 'Todos', mobileLabel: 'Todos', icon: '◉' },
    {
      key: 'comments',
      label: 'Bot Comentarios',
      mobileLabel: 'Bot Co…',
      icon: '◎',
    },
    {
      key: 'automations',
      label: 'Bot Automatizaciones',
      mobileLabel: 'Bot Aut…',
      icon: '⚙',
    },
    {
      key: 'scheduled',
      label: 'Bot Programados',
      mobileLabel: 'Bot Pr…',
      icon: '◷',
    },
    { key: 'team', label: 'Equipo', icon: '♟' },
    { key: 'tasks', label: 'Tareas', icon: '✓' },
    { key: 'urgent', label: 'Urgentes', icon: '!' },
  ].map(filter => ({
    ...filter,
    count: threads.value.filter(thread =>
      matchesInboxFilter(thread, filter.key)
    ).length,
  }))
);

const threadActivityTime = thread =>
  new Date(
    thread.last_activity_at || thread.updated_at || thread.created_at || 0
  ).getTime();

const filteredThreads = computed(() => {
  if (activeInboxFilter.value === 'tasks' && taskView.value === 'inbox') {
    return threads.value.filter(thread => matchesInboxFilter(thread, 'team'));
  }
  let filtered = threads.value.filter(thread =>
    matchesInboxFilter(thread, activeInboxFilter.value)
  );
  if (activeInboxFilter.value === 'tasks') {
    filtered = filtered.filter(thread => {
      const task = threadTask(thread);
      if (!task) return false;
      if (isFollowupMode.value) {
        if (task.task_status !== 'postponed') return false;
        if (taskView.value === 'waiting') return !task.due_at;
        if (!task.due_at) return false;
        const due = new Date(task.due_at);
        const today = new Date();
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);
        if (taskView.value === 'today') return due <= endOfToday;
        return due > endOfToday;
      }
      if (['completed', 'postponed'].includes(task.task_status)) return false;
      if (taskView.value === 'created') {
        return Number(task.sender?.id) === Number(currentUserId.value);
      }
      if (taskView.value === 'team') return true;
      return Number(task.assigned_to?.id) === Number(currentUserId.value);
    });
  }
  if (activeInboxFilter.value !== 'comments') return filtered;

  // Bot Comentarios se trabaja como una cola: el mas antiguo primero y los
  // nuevos se incorporan al final, sin alterar el orden de otros buzones.
  return [...filtered].sort(
    (first, second) => threadActivityTime(first) - threadActivityTime(second)
  );
});

const visibleThreadName = name =>
  String(name || '')
    .replace(/^Tarea ·\s*/i, '')
    .trim();

const activeName = computed(() => {
  if (!activeThread.value) return 'Selecciona una conversación interna';
  if (activeThread.value.name) {
    return visibleThreadName(activeThread.value.name);
  }
  return activeThread.value.members
    .filter(member => member.id !== currentUserId.value)
    .map(member => member.name)
    .join(', ');
});

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatTime = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const taskCustomerName = message => {
  const firstLine = String(message?.body || '')
    .split('\n')[0]
    .trim();
  const patterns = [
    /Clienta que quiere pagar:\s*(.+?)(?:\s+\(@|$)/i,
    /Seguimiento pendiente de\s+(.+?)(?:\s+\(@|$)/i,
    /Seguimiento de pago(?: de prueba)?:\s*(.+)$/i,
    /Seguimiento de pedido\s+(@?[^·]+)/i,
    /Responder conversación de (?:Instagram|WhatsApp|Messenger)\s+(@?\S+)/i,
    /Revisar envío fallido:\s*(.+)$/i,
  ];
  const match = patterns.map(pattern => firstLine.match(pattern)).find(Boolean);
  return (match?.[1] || firstLine.replace(/^[^\p{L}\p{N}@]+/u, ''))
    .replace(/\s+\(@[^)]+\)$/, '')
    .trim();
};

const taskConversationExcerpt = message => {
  const value = String(message?.body || '');
  const quoted = value.match(/«([\s\S]*?)»/);
  if (quoted?.[1]) return quoted[1].replace(/\s+/g, ' ').trim();
  return (
    value
      .split('\n')
      .slice(1)
      .map(line => line.trim())
      .find(
        line => line && !/^(Canal:|Qué tienes que hacer:|\[)/i.test(line)
      ) || ''
  );
};

const threadPreview = thread => {
  const message = threadTask(thread) || thread.messages?.[0];
  if (!message) {
    return thread.thread_type === 'group_chat'
      ? `Grupo · ${thread.members.length} participantes · Sin mensajes`
      : 'Conversación nueva';
  }
  if (message.kind !== 'task') return message.body || 'Conversación nueva';
  const name = taskCustomerName(message);
  const excerpt = taskConversationExcerpt(message);
  return [name, excerpt].filter(Boolean).join(' · ');
};

const isTaskOverdue = thread => {
  const task = threadTask(thread);
  if (!task?.due_at || ['completed', 'postponed'].includes(task.task_status)) {
    return false;
  }
  const overdueThreshold = new Date();
  overdueThreshold.setDate(overdueThreshold.getDate() - 2);
  return new Date(task.due_at) < overdueThreshold;
};

const taskViews = computed(() =>
  isFollowupMode.value
    ? [
        { key: 'today', label: 'Hoy' },
        { key: 'upcoming', label: 'Próximos' },
        { key: 'waiting', label: 'Esperando' },
      ]
    : [
        { key: 'mine', label: 'Para mí' },
        { key: 'created', label: 'Creadas por mí' },
        { key: 'team', label: 'Equipo' },
        { key: 'inbox', label: 'Bandeja de entrada' },
      ]
);

const dueShortcut = days => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(9, 0, 0, 0);
  newTaskDueAt.value = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const messageDisplayBody = message => {
  const value = String(message?.body || '');
  if (value.startsWith('[[mi_marciana_card]]')) return '';
  if (message?.kind !== 'task') return value;
  if (!value.includes('\n')) return value;
  let visibleLines = value
    .split('\n')
    .slice(1)
    .filter(line => !/^Abrir conversación:\s*https?:\/\//i.test(line.trim()))
    .filter(
      line => !/^\[\[instagram_comment_button:[1-8]:.*\]\]$/.test(line.trim())
    )
    .filter(line => !/^\[[^\]]+\]$/.test(line.trim()));

  if (/Nuevo comentario para revisar/i.test(value)) {
    const detailsStart = visibleLines.findIndex(line =>
      /^(Producto Shopify:|OPCIONES?\s|QUÉ PUEDES RESPONDERME)/i.test(
        line.trim()
      )
    );
    if (detailsStart >= 0) visibleLines = visibleLines.slice(0, detailsStart);
    visibleLines = visibleLines.map(line =>
      line
        .replace(/^Familia:\s*question$/i, 'Familia: Consulta')
        .replace(/^Familia:\s*gratitude$/i, 'Familia: Agradecimiento')
        .replace(/^Familia:\s*review$/i, 'Familia: Pendiente de clasificar')
        .replace(
          /^Acción:\s*public_and_private$/i,
          'Acción: Respuesta pública con mensaje privado'
        )
        .replace(
          /^Acción:\s*public_only$/i,
          'Acción: Respuesta pública sin mensaje privado'
        )
    );
  }

  return visibleLines.join('\n').trim();
};

const sharedCatalogCard = message => {
  const value = String(message?.body || '');
  const marker = '[[mi_marciana_card]]';
  if (!value.startsWith(marker)) return null;
  try {
    return JSON.parse(value.slice(marker.length));
  } catch {
    return null;
  }
};

const latestCommentActionMessageId = computed(() => {
  if (!activeThread.value || !isBotThread(activeThread.value, 'comments')) {
    return null;
  }
  return [...messages.value]
    .reverse()
    .find(message =>
      /\[instagram_comment_review:\d+\]/.test(message.body || '')
    )?.id;
});

const numberedOptions = value => {
  const visible = [...String(value || '').matchAll(/^\s*([1-8])\.\s+(.+)$/gm)];
  const hidden = [
    ...String(value || '').matchAll(
      /^\[\[instagram_comment_button:([1-8]):(.+)\]\]$/gm
    ),
  ];
  return [...visible, ...hidden].map(match => ({
    number: match[1],
    text: match[2]
      .replace(/^PÚBLICA:\s*/i, '')
      .replace(/[«»]/g, '')
      .trim(),
  }));
};

const commentQuickActions = message => {
  if (message.id !== latestCommentActionMessageId.value) return [];
  const value = String(message.body || '');
  const kisses = { label: '😘😘', value: '😘😘', tone: 'secondary' };

  if (/Necesito identificar la prenda/i.test(value)) {
    const candidates = numberedOptions(value)
      .filter(option => Number(option.number) <= 5)
      .map(option => ({
        label: `${option.number} · ${option.text}`,
        value: option.number,
        tone: 'primary',
      }));
    return [
      ...candidates,
      {
        label: 'Volver a identificar',
        value: 'Volver a identificar',
        tone: 'secondary',
      },
      {
        label: 'Sin vinculación · solo contestar',
        value: 'Ninguna',
        tone: 'neutral',
      },
      ...[2, 3, 4, 5].map(count => ({
        label: `${count} productos`,
        value: `${count} productos`,
        tone: 'secondary',
      })),
      kisses,
    ];
  }

  if (/Ahora dime cuál corresponde a este comentario/i.test(value)) {
    return [
      ...numberedOptions(value).map(option => ({
        label: `${option.number} · ${option.text}`,
        value: option.number,
        tone: 'primary',
      })),
      {
        label: 'Comentario general · solo contestar',
        value: 'Ninguna',
        tone: 'neutral',
      },
      kisses,
    ];
  }

  if (/¿Quieres que memorice/i.test(value)) {
    return [
      { label: 'Memorizar', value: 'Memorizar', tone: 'primary' },
      { label: 'Solo esta vez', value: 'No memorizar', tone: 'neutral' },
    ];
  }

  if (/Nuevo comentario para revisar/i.test(value)) {
    const options = numberedOptions(value).map(option => ({
      label: `${option.number} · ${option.text}`,
      value: option.number,
      tone: Number(option.number) >= 5 ? 'secondary' : 'primary',
    }));
    options.splice(3, 0, {
      label: '4 · Escribo yo',
      value: '4',
      tone: 'neutral',
    });
    const actions = [
      ...options,
      { label: 'No responder', value: 'No procede', tone: 'neutral' },
      { label: 'Descartar', value: 'No enviar', tone: 'danger' },
      kisses,
    ];
    if (/no está asociado a ningún producto/i.test(value)) {
      actions.splice(options.length, 0, {
        label: 'Volver a identificar',
        value: 'Volver a identificar',
        tone: 'secondary',
      });
    } else {
      actions.splice(options.length, 0, {
        label: 'Volver a identificar',
        value: 'Volver a identificar',
        tone: 'secondary',
      });
    }
    return actions;
  }

  if (/Para aprender, dime el motivo/i.test(value)) {
    return numberedOptions(value).map(option => ({
      label: `${option.number} · ${option.text}`,
      value: option.number,
      tone: 'neutral',
    }));
  }
  return [];
};

const commentQuickActionGroups = message => {
  const actions = commentQuickActions(message);
  if (!actions.length) return [];
  if (!/Nuevo comentario para revisar/i.test(String(message.body || ''))) {
    return [{ title: '', actions }];
  }

  const publicOnly = actions.filter(action =>
    ['1', '2', '3'].includes(String(action.value))
  );
  const publicAndPrivate = actions.filter(action =>
    ['5', '6', '7'].includes(String(action.value))
  );
  const otherValues = [
    '4',
    'Asociar producto',
    'No procede',
    '😘😘',
    'No enviar',
  ];
  const other = otherValues.flatMap(value =>
    actions.filter(action => String(action.value) === value)
  );

  return [
    { title: 'Respuesta pública sin mensaje privado', actions: publicOnly },
    {
      title: 'Respuesta pública con mensaje privado',
      actions: publicAndPrivate,
    },
    { title: 'Otras acciones', actions: other },
  ].filter(group => group.actions.length);
};

const conversationUrl = conversationId =>
  `/app/accounts/${accountId.value}/conversations/${conversationId}`;

const openLinkedConversation = conversationId => {
  if (!conversationId) return;
  const returnPath = router.resolve({
    path: route.path,
    query: {
      ...route.query,
      filter: 'tasks',
      task_view: isFollowupMode.value ? 'followups' : taskView.value,
      followup_view: isFollowupMode.value ? taskView.value : undefined,
      thread_id: activeThread.value?.id || undefined,
    },
  }).fullPath;
  window.sessionStorage.setItem(
    `mm-chatwoot:conversation-return:${accountId.value}:${conversationId}`,
    JSON.stringify({
      conversationId,
      fullPath: returnPath,
    })
  );
  router.push({
    path: conversationUrl(conversationId),
    query: { mm_return_to: returnPath },
  });
};

const leaveInternalInbox = () => {
  router.push({
    name: 'home',
    params: { accountId: accountId.value },
  });
};

const loadThreads = async ({ append = false, page = 1 } = {}) => {
  if (append) loadingMore.value = true;
  else loading.value = true;
  try {
    const { data } = await axios.get(`${baseUrl.value}/internal_threads`, {
      params: {
        page,
        per_page: 75,
        filter:
          activeInboxFilter.value === 'tasks' && taskView.value !== 'inbox'
            ? 'tasks'
            : undefined,
      },
    });
    threads.value = append
      ? [
          ...threads.value,
          ...data.threads.filter(
            thread => !threads.value.some(existing => existing.id === thread.id)
          ),
        ]
      : data.threads;
    agents.value = data.agents;
    currentUserId.value = data.current_user_id;
    threadPage.value = data.meta?.page || page;
    threadsHaveMore.value = Boolean(data.meta?.has_more);
  } finally {
    if (append) loadingMore.value = false;
    else loading.value = false;
  }
};

const loadMoreThreads = () =>
  loadThreads({ append: true, page: threadPage.value + 1 });

const openThread = async thread => {
  const { data } = await axios.get(
    `${baseUrl.value}/internal_threads/${thread.id}`
  );
  activeThread.value = data;
  messages.value = data.messages;
  const listThread = threads.value.find(item => item.id === thread.id);
  if (listThread) listThread.unread_count = 0;
  await nextTick();
  messageList.value?.scrollTo({ top: messageList.value.scrollHeight });
};

const refreshBotCommentThread = async (threadId, attempt = 0) => {
  const delays = [700, 1400, 2800, 5000];
  if (attempt >= delays.length) return;

  await new Promise(resolve => {
    window.setTimeout(resolve, delays[attempt]);
  });
  await loadThreads();
  if (threads.value.some(thread => thread.id === threadId)) {
    await refreshBotCommentThread(threadId, attempt + 1);
    return;
  }

  if (activeThread.value?.id === threadId) {
    activeThread.value = null;
    messages.value = [];
    if (
      filteredThreads.value.length &&
      window.matchMedia('(min-width: 768px)').matches
    ) {
      await openThread(filteredThreads.value[0]);
    }
  }
};

const selectInboxFilter = async filter => {
  activeInboxFilter.value = filter;
  activeThread.value = null;
  messages.value = [];
  threads.value = [];
  threadPage.value = 1;
  threadsHaveMore.value = false;
  await loadThreads();

  if (
    !activeThread.value &&
    filteredThreads.value.length &&
    window.matchMedia('(min-width: 768px)').matches
  ) {
    await openThread(filteredThreads.value[0]);
  }
};

const selectTaskView = async view => {
  const changesDataSource =
    (taskView.value === 'inbox') !== (view === 'inbox');
  taskView.value = view;
  activeThread.value = null;
  messages.value = [];
  if (changesDataSource) {
    threads.value = [];
    threadPage.value = 1;
    threadsHaveMore.value = false;
    await loadThreads();
  }

  if (
    filteredThreads.value.length &&
    window.matchMedia('(min-width: 768px)').matches
  ) {
    await openThread(filteredThreads.value[0]);
  }
};

const scrollFilters = event => {
  if (!filtersNav.value || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
    return;
  }
  filtersNav.value.scrollLeft += event.deltaY;
};

const scrollFiltersBy = distance => {
  if (!filtersNav.value) return;
  filtersNav.value.scrollLeft += distance;
};

const resizeInbox = event => {
  if (!isResizingInbox.value) return;
  inboxPanelWidth.value = Math.min(
    560,
    Math.max(260, resizeStartWidth + event.clientX - resizeStartX)
  );
};

const stopInboxResize = () => {
  if (!isResizingInbox.value) return;
  isResizingInbox.value = false;
  document.body.style.removeProperty('cursor');
  document.body.style.removeProperty('user-select');
  localStorage.setItem(
    'mi-marciana-internal-inbox-width',
    inboxPanelWidth.value
  );
  window.removeEventListener('pointermove', resizeInbox);
  window.removeEventListener('pointerup', stopInboxResize);
};

const startInboxResize = event => {
  resizeStartX = event.clientX;
  resizeStartWidth = inboxPanelWidth.value;
  isResizingInbox.value = true;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('pointermove', resizeInbox);
  window.addEventListener('pointerup', stopInboxResize);
};

const createThread = async () => {
  if (!newMemberIds.value.length) return;
  sendError.value = '';
  const selectedNames = agents.value
    .filter(agent => newMemberIds.value.includes(agent.id))
    .map(agent => agent.name)
    .filter(Boolean);
  const requestedName = newThreadName.value.trim();
  const fallbackName = selectedNames.length
    ? selectedNames.join(', ')
    : 'Grupo de trabajo';
  const visibleName = requestedName || fallbackName;
  try {
    const { data } = await axios.post(`${baseUrl.value}/internal_threads`, {
      name: visibleName,
      member_ids: newMemberIds.value,
      thread_type: newMemberIds.value.length > 1 ? 'group' : 'direct',
    });
    if (route.query.create === 'project' && linkedConversationId.value) {
      await axios.post(
        `${baseUrl.value}/conversations/${linkedConversationId.value}/custom_attributes`,
        {
          custom_attributes: {
            mi_marciana_project: visibleName,
            mi_marciana_project_thread_id: data.id,
          },
        }
      );
    }
    showNewThread.value = false;
    newThreadName.value = '';
    newMemberIds.value = [];
    await loadThreads();
    await openThread(data);
  } catch {
    sendError.value = 'No se ha podido crear el grupo. Inténtalo de nuevo.';
  }
};

const resetNewTask = () => {
  newTaskBody.value = '';
  newTaskResponsibleId.value = '';
  newTaskParticipantIds.value = [];
  newTaskDueAt.value = '';
  newTaskFollowUp.value = false;
};

const createStandaloneTask = async () => {
  if (!newTaskBody.value.trim() || !newTaskResponsibleId.value) return;
  const taskIsMine =
    Number(newTaskResponsibleId.value) === Number(currentUserId.value);
  const taskIsFollowUp = newTaskFollowUp.value;
  creatingTask.value = true;
  sendError.value = '';
  try {
    const memberIds = [
      newTaskResponsibleId.value,
      ...newTaskParticipantIds.value,
    ].filter((id, index, values) => values.indexOf(id) === index);
    const { data: thread } = await axios.post(
      `${baseUrl.value}/internal_threads`,
      {
        name: `Tarea · ${newTaskBody.value.trim().slice(0, 64)}`,
        member_ids: memberIds,
        thread_type: memberIds.length > 1 ? 'group' : 'direct',
      }
    );
    const payload = {
      body: newTaskBody.value.trim(),
      kind: 'task',
      assigned_to_id: newTaskResponsibleId.value,
      due_at: newTaskDueAt.value || null,
      priority: 'normal',
      conversation_id: linkedConversationId.value || null,
    };
    const { data: task } = await axios.post(
      `${baseUrl.value}/internal_threads/${thread.id}/internal_messages`,
      payload
    );
    if (taskIsFollowUp) {
      await axios.patch(`${baseUrl.value}/internal_messages/${task.id}`, {
        task_status: 'postponed',
      });
    }
    showNewTask.value = false;
    resetNewTask();
    activeInboxFilter.value = 'tasks';
    isFollowupMode.value = taskIsFollowUp;
    taskView.value = isFollowupMode.value ? 'today' : taskIsMine ? 'mine' : 'team';
    await loadThreads();
    const created = threads.value.find(item => item.id === thread.id);
    if (created) await openThread(created);
  } catch {
    sendError.value = 'No se ha podido crear la tarea. Inténtalo de nuevo.';
  } finally {
    creatingTask.value = false;
  }
};

const send = async (files = []) => {
  const attachments = Array.isArray(files) ? files : [];
  if (!activeThread.value || (!body.value.trim() && !attachments.length)) {
    return;
  }
  if (isSending.value) return;
  isSending.value = true;
  sendError.value = '';
  const sentThreadId = activeThread.value.id;
  const refreshAfterSend = isBotThread(activeThread.value, 'comments');
  const payload = new FormData();
  payload.append('body', body.value.trim());
  payload.append('kind', composeType.value);
  if (linkedConversationId.value) {
    payload.append('conversation_id', linkedConversationId.value);
  }
  if (composeType.value === 'task') {
    if (!assignedToId.value) return;
    payload.append('assigned_to_id', assignedToId.value);
    if (dueAt.value) payload.append('due_at', dueAt.value);
    payload.append('priority', priority.value);
  }
  attachments.forEach(file => payload.append('attachments[]', file));
  try {
    const { data } = await axios.post(
      `${baseUrl.value}/internal_threads/${activeThread.value.id}/internal_messages`,
      payload,
      { timeout: 15000 }
    );
    messages.value.push(data);
    if (
      composeType.value === 'task' &&
      assignedToId.value &&
      !activeThread.value.members.some(
        member => String(member.id) === String(assignedToId.value)
      )
    ) {
      const assignedAgent = agents.value.find(
        agent => String(agent.id) === String(assignedToId.value)
      );
      if (assignedAgent) activeThread.value.members.push(assignedAgent);
    }
    body.value = '';
    composeType.value = 'message';
    showComposerMenu.value = false;
    await nextTick();
    messageList.value?.scrollTo({
      top: messageList.value.scrollHeight,
      behavior: 'smooth',
    });
    if (refreshAfterSend) refreshBotCommentThread(sentThreadId);
  } catch {
    sendError.value =
      'No se ha podido enviar. El texto sigue aquí para que puedas intentarlo otra vez.';
  } finally {
    isSending.value = false;
  }
};

const sendQuickReply = async value => {
  if (isSending.value) return;
  quickReplyValue.value = String(value);
  body.value = String(value);
  await nextTick();
  await send();
  quickReplyValue.value = '';
};

const syncMobileViewport = () => {
  if (!window.matchMedia('(max-width: 767px)').matches) {
    mobileViewportStyle.value = {};
    return;
  }
  const viewport = window.visualViewport;
  mobileViewportStyle.value = viewport
    ? {
        height: `${viewport.height}px`,
        transform: `translateY(${viewport.offsetTop}px)`,
      }
    : { height: '100dvh' };
};

const conversationLabel = conversation => {
  const sender = conversation?.meta?.sender || {};
  return (
    sender.name || sender.available_name || `Conversación #${conversation.id}`
  );
};

const conversationUsername = conversation => {
  const sender = conversation?.meta?.sender || {};
  return (
    sender.additional_attributes?.social_instagram_user_name ||
    sender.phone_number ||
    sender.email ||
    ''
  );
};

const conversationAvatar = conversation => {
  const sender = conversation?.meta?.sender || {};
  return sender.thumbnail || sender.avatar_url || sender.avatar || '';
};

const conversationChannel = conversation => {
  const channel = String(conversation?.meta?.channel || '').toLowerCase();
  if (channel.includes('instagram')) return { icon: '📸', label: 'Instagram' };
  if (channel.includes('whatsapp')) return { icon: '💬', label: 'WhatsApp' };
  if (channel.includes('facebook')) return { icon: '●', label: 'Messenger' };
  if (channel.includes('email')) return { icon: '✉️', label: 'Correo' };
  return { icon: '💬', label: 'Chat' };
};

const filteredConversations = computed(() => {
  const query = conversationSearch.value.trim().toLocaleLowerCase('es');
  if (!query) return recentConversations.value;
  return recentConversations.value.filter(conversation =>
    [
      conversationLabel(conversation),
      conversationUsername(conversation),
      conversation.id,
    ]
      .join(' ')
      .toLocaleLowerCase('es')
      .includes(query)
  );
});

const loadRecentConversations = async () => {
  try {
    const { data } = await axios.get(`${baseUrl.value}/conversations`, {
      params: { status: 'all', page: 1 },
    });
    recentConversations.value = data?.data?.payload || data?.payload || [];
  } catch {
    recentConversations.value = [];
  }
};

const onFilesSelected = async event => {
  const files = Array.from(event.target.files || []);
  if (files.length) await send(files);
  event.target.value = '';
};

const selectTask = () => {
  composeType.value = 'task';
  showComposerMenu.value = false;
};

const openCatalogSearch = type => {
  catalogSearchType.value = type;
  catalogQuery.value = '';
  catalogResults.value = [];
  catalogError.value = '';
  showCatalogSearch.value = true;
  showComposerMenu.value = false;
};

const closeCatalogSearch = () => {
  showCatalogSearch.value = false;
  catalogResults.value = [];
  catalogError.value = '';
};

const searchCatalog = async () => {
  if (catalogSearchTimer) {
    window.clearTimeout(catalogSearchTimer);
    catalogSearchTimer = null;
  }
  const query = catalogQuery.value.trim();
  if (query.length < 2 || catalogLoading.value) return;
  catalogLoading.value = true;
  catalogError.value = '';
  catalogResults.value = [];
  try {
    const endpoint =
      catalogSearchType.value === 'product' ? 'products' : 'orders';
    const { data } = await axios.get(
      `${baseUrl.value}/internal_catalog/${endpoint}`,
      { params: { q: query }, timeout: 15000 }
    );
    catalogResults.value =
      catalogSearchType.value === 'product'
        ? data.products || []
        : data.order
          ? [data.order]
          : [];
    if (!catalogResults.value.length) {
      catalogError.value = 'No se ha encontrado ningún resultado.';
    }
  } catch (error) {
    catalogError.value =
      error.response?.data?.error || 'No se ha podido completar la búsqueda.';
  } finally {
    catalogLoading.value = false;
  }
};

watch(catalogQuery, value => {
  if (catalogSearchTimer) window.clearTimeout(catalogSearchTimer);
  if (!showCatalogSearch.value || value.trim().length < 2) return;

  catalogSearchTimer = window.setTimeout(searchCatalog, 400);
});

const sendCatalogCard = async item => {
  const isProduct = catalogSearchType.value === 'product';
  const shopifyProductId = String(item.id || '')
    .split('/')
    .filter(Boolean)
    .pop();
  const card = isProduct
    ? {
        type: 'product',
        title: item.name || item.title || 'Producto',
        image: item.image || item.images?.[0]?.url || '',
        reference: item.ref || item.sku || '',
        price: item.price?.formatted || '',
        stock: Number(item.totalStock || 0),
        url: item.handle
          ? `https://www.mimarciana.com/products/${item.handle}`
          : '',
        adminUrl: shopifyProductId
          ? `https://admin.shopify.com/store/2370d3-3/products/${shopifyProductId}`
          : '',
      }
    : {
        type: 'order',
        title: `Pedido ${item.name || ''}`.trim(),
        customer: item.customer?.name || 'Sin clienta',
        phone: item.customer?.phone || '',
        total: item.total?.formatted || '',
        financialStatus: item.displayFinancialStatus || '',
        fulfillmentStatus: item.displayFulfillmentStatus || '',
      };
  body.value = `[[mi_marciana_card]]${JSON.stringify(card)}`;
  await send([]);
  if (!sendError.value) closeCatalogSearch();
};

const toggleRecording = async () => {
  if (isRecording.value) {
    mediaRecorder?.stop();
    return;
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    window.alert('Este navegador no permite grabar audio.');
    return;
  }

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    window.alert('No se ha podido acceder al micrófono. Revisa el permiso.');
    return;
  }
  audioChunks = [];
  mediaRecorder = new MediaRecorder(stream);
  mediaRecorder.ondataavailable = event => audioChunks.push(event.data);
  mediaRecorder.onstop = async () => {
    const mimeType = mediaRecorder.mimeType || 'audio/webm';
    const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
    const audioFile = new File(
      [new Blob(audioChunks, { type: mimeType })],
      `audio-${Date.now()}.${extension}`,
      { type: mimeType }
    );
    stream.getTracks().forEach(track => track.stop());
    isRecording.value = false;
    await send([audioFile]);
  };
  mediaRecorder.start();
  isRecording.value = true;
};

const updateTask = async (message, taskStatus) => {
  sendError.value = '';
  try {
    const { data } = await axios.patch(
      `${baseUrl.value}/internal_messages/${message.id}`,
      {
        task_status: taskStatus,
      }
    );
    Object.assign(message, data);

    if (activeThread.value?.task?.id === message.id) {
      Object.assign(activeThread.value.task, data);
    }
    const listThread = threads.value.find(
      thread => thread.id === activeThread.value?.id
    );
    if (listThread?.task?.id === message.id) {
      Object.assign(listThread.task, data);
    }
    const openMessage = messages.value.find(item => item.id === message.id);
    if (openMessage && openMessage !== message) {
      Object.assign(openMessage, data);
    }

    await loadThreads();
  } catch {
    sendError.value = 'No se ha podido actualizar la tarea. Inténtalo de nuevo.';
  }
};

const postponeTask = async (message, days) => {
  const due = new Date();
  due.setDate(due.getDate() + days);
  due.setHours(9, 0, 0, 0);
  const { data } = await axios.patch(
    `${baseUrl.value}/internal_messages/${message.id}`,
    { task_status: 'postponed', due_at: due.toISOString() }
  );
  Object.assign(message, data);
  if (activeThread.value?.task?.id === message.id) {
    Object.assign(activeThread.value.task, data);
  }
};

onMounted(async () => {
  syncMobileViewport();
  window.visualViewport?.addEventListener('resize', syncMobileViewport);
  window.visualViewport?.addEventListener('scroll', syncMobileViewport);
  const savedInboxWidth = Number(
    localStorage.getItem('mi-marciana-internal-inbox-width')
  );
  if (savedInboxWidth >= 260 && savedInboxWidth <= 560) {
    inboxPanelWidth.value = savedInboxWidth;
  }
  await loadThreads();
  const requestedTaskView = String(route.query.task_view || '');
  if (requestedTaskView === 'followups') {
    isFollowupMode.value = true;
    const requestedFollowupView = String(route.query.followup_view || 'today');
    if (taskViews.value.some(view => view.key === requestedFollowupView)) {
      taskView.value = requestedFollowupView;
    }
  } else if (taskViews.value.some(view => view.key === requestedTaskView)) {
    taskView.value = requestedTaskView;
  }
  const requestedThreadId = Number(route.query.thread_id);
  if (requestedThreadId) {
    const requestedThread = threads.value.find(
      thread => Number(thread.id) === requestedThreadId
    );
    if (requestedThread) await openThread(requestedThread);
  }
  if (route.query.compose === 'task') {
    activeInboxFilter.value = 'tasks';
    showNewTask.value = true;
    await loadRecentConversations();
  }
  if (route.query.create === 'project') {
    showNewThread.value = true;
    newThreadName.value = route.query.contact_name
      ? `Proyecto · ${route.query.contact_name}`
      : 'Nuevo proyecto';
  }
  if (
    filteredThreads.value.length &&
    window.matchMedia('(min-width: 768px)').matches
  ) {
    await openThread(filteredThreads.value[0]);
  }
  window.addEventListener(
    'mi-marciana:internal-message',
    handleIncomingMessage
  );
});

const handleIncomingMessage = async event => {
  const incoming = event.detail;
  await loadThreads();
  if (activeThread.value?.id === incoming.thread_id) {
    messages.value.push(incoming);
    await nextTick();
    messageList.value?.scrollTo({
      top: messageList.value.scrollHeight,
      behavior: 'smooth',
    });
  }
};

onBeforeUnmount(() => {
  if (catalogSearchTimer) window.clearTimeout(catalogSearchTimer);
  stopInboxResize();
  window.visualViewport?.removeEventListener('resize', syncMobileViewport);
  window.visualViewport?.removeEventListener('scroll', syncMobileViewport);
  window.removeEventListener(
    'mi-marciana:internal-message',
    handleIncomingMessage
  );
});
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full bg-n-background text-n-slate-12 max-md:fixed max-md:inset-0 max-md:z-[60] max-md:h-[100dvh]"
    :style="mobileViewportStyle"
    style="color-scheme: light"
  >
    <aside
      class="relative w-full max-md:!w-full shrink-0 border-r border-n-weak flex flex-col"
      :class="activeThread ? 'hidden md:flex' : 'flex'"
      :style="{ width: `${inboxPanelWidth}px` }"
    >
      <div
        class="h-16 shrink-0 px-3 flex items-center justify-between border-b border-n-weak bg-n-solid-1"
      >
        <div class="flex min-w-0 items-center gap-2">
          <button
            class="grid size-11 shrink-0 place-items-center rounded-full text-2xl md:hidden"
            aria-label="Volver a las conversaciones"
            @click="leaveInternalInbox"
          >
            ‹
          </button>
          <div>
            <h1 class="text-xl font-bold">{{ isFollowupMode ? 'Seguimientos' : activeInboxFilter === 'tasks' ? 'Tareas' : 'Mi bandeja' }}</h1>
            <p class="text-xs text-n-slate-10">{{ isFollowupMode ? 'Casos que hay que volver a comprobar' : taskView === 'inbox' ? 'Mensajes y grupos del equipo' : 'Tareas del equipo' }}</p>
          </div>
        </div>
        <div class="flex items-center gap-1.5">
          <button
            class="h-9 rounded-xl bg-amber-400 px-3 text-sm font-bold text-slate-950"
            title="Crear tarea"
            @click="showNewTask = true"
          >
            + Tarea
          </button>
          <button
            v-if="activeInboxFilter !== 'tasks' || taskView === 'inbox'"
            class="size-9 rounded-xl bg-n-brand text-white text-xl"
            title="Nuevo chat o grupo"
            @click="showNewThread = true"
          >
            +
          </button>
        </div>
      </div>
      <div
        v-if="activeInboxFilter !== 'tasks'"
        class="flex shrink-0 items-stretch border-b border-n-weak bg-n-solid-1"
      >
        <button
          type="button"
          class="hidden w-7 shrink-0 place-items-center border-r border-n-weak text-lg text-n-slate-11 hover:bg-n-alpha-2 md:grid"
          aria-label="Ver bandejas anteriores"
          @click="scrollFiltersBy(-180)"
        >
          ‹
        </button>
        <nav
          ref="filtersNav"
          class="flex min-w-0 flex-1 gap-1 overflow-x-auto px-2 py-1.5 md:gap-1.5 md:px-2 md:py-2 [scrollbar-width:thin]"
          aria-label="Bandejas internas"
          @wheel="scrollFilters"
        >
          <button
            v-for="filter in inboxFilters"
            :key="filter.key"
            type="button"
            class="flex h-7 shrink-0 items-center gap-1 rounded-full border px-2 text-[11px] font-semibold transition-colors md:h-8 md:gap-1.5 md:px-2.5 md:text-xs"
            :class="
              activeInboxFilter === filter.key
                ? 'border-n-slate-12 bg-n-slate-12 text-n-slate-1'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            :aria-pressed="activeInboxFilter === filter.key"
            @click="selectInboxFilter(filter.key)"
          >
            <span aria-hidden="true">{{ filter.icon }}</span>
            <span class="md:hidden">{{
              filter.mobileLabel || filter.label
            }}</span>
            <span class="hidden md:inline">{{ filter.label }}</span>
            <span
              class="grid h-5 min-w-5 place-items-center rounded-full px-1 text-[10px]"
              :class="
                activeInboxFilter === filter.key
                  ? 'bg-white/20 text-white'
                  : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              {{ filter.count }}
            </span>
          </button>
        </nav>
        <button
          type="button"
          class="hidden w-7 shrink-0 place-items-center border-l border-n-weak text-lg text-n-slate-11 hover:bg-n-alpha-2 md:grid"
          aria-label="Ver más bandejas"
          @click="scrollFiltersBy(180)"
        >
          ›
        </button>
      </div>
      <nav
        v-if="activeInboxFilter === 'tasks'"
        class="flex shrink-0 gap-1 overflow-x-auto border-b border-n-weak bg-amber-50 px-2 py-2"
        aria-label="Vistas de tareas"
      >
        <button
          v-for="view in taskViews"
          :key="view.key"
          type="button"
          class="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
          :class="taskView === view.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'"
          @click="selectTaskView(view.key)"
        >
          {{ view.label }}
        </button>
      </nav>
      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div v-if="loading" class="p-5 text-n-slate-10">Cargando…</div>
        <div
          v-else-if="!filteredThreads.length"
          class="p-6 text-center text-n-slate-10"
        >
          <div class="text-4xl mb-3">💬</div>
          <template v-if="activeInboxFilter === 'all'">
            Todavía no tienes mensajes internos.<br />Pulsa + para comenzar.
          </template>
          <template v-else>No hay elementos en esta bandeja.</template>
        </div>
        <button
          v-for="thread in filteredThreads"
          :key="thread.id"
          class="relative flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-n-alpha-1 after:absolute after:bottom-0 after:left-[4.5rem] after:right-0 after:h-px after:bg-n-weak"
          :class="activeThread?.id === thread.id ? 'bg-n-alpha-2' : ''"
          @click="openThread(thread)"
        >
          <div
            class="relative grid size-11 shrink-0 place-items-center overflow-visible rounded-full font-bold"
            :class="
              thread.thread_type === 'group_chat'
                ? 'bg-black'
                : 'bg-pink-100 text-pink-700'
            "
          >
            <span
              v-if="thread.thread_type === 'group_chat'"
              class="text-xl text-white"
              aria-label="Grupo"
            >👥</span>
            <template v-else>
              {{
                (
                  visibleThreadName(thread.name) ||
                  thread.members
                    .filter(m => m.id !== currentUserId)
                    .map(m => m.name)
                    .join(', ')
                )
                  .slice(0, 2)
                  .toUpperCase()
              }}
              <img
                :src="brandLogoUrl"
                alt="Mi Marciana"
                class="absolute -bottom-0.5 -right-0.5 size-[18px] rounded-full border-2 border-white object-cover shadow-sm"
              />
            </template>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-2">
              <div class="flex min-w-0 items-center gap-1.5">
                <strong class="truncate text-sm font-semibold">{{
                  visibleThreadName(thread.name) ||
                  thread.members
                    .filter(m => m.id !== currentUserId)
                    .map(m => m.name)
                    .join(', ')
                }}</strong>
                <span
                  v-if="thread.thread_type === 'group_chat'"
                  class="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-700"
                >Grupo</span>
              </div>
              <span
                v-if="thread.unread_count"
                class="grid h-5 min-w-5 place-items-center rounded-full bg-n-brand px-1.5 text-xs text-white"
                >{{ thread.unread_count }}</span
              >
            </div>
            <p class="mt-0.5 truncate text-sm text-n-slate-10">
              {{ threadPreview(thread) }}
            </p>
            <span
              v-if="isTaskOverdue(thread)"
              class="mt-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-red-700"
            >
              Atrasada
            </span>
          </div>
        </button>
        <button
          v-if="threadsHaveMore"
          type="button"
          class="mx-auto my-3 block rounded-xl border border-n-weak bg-n-solid-1 px-4 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-60"
          :disabled="loadingMore"
          @click="loadMoreThreads"
        >
          {{ loadingMore ? 'Cargando…' : 'Cargar conversaciones anteriores' }}
        </button>
      </div>
      <button
        type="button"
        class="absolute -right-1 top-0 z-20 hidden h-full w-2 cursor-col-resize touch-none md:block"
        :class="isResizingInbox ? 'bg-n-brand/20' : 'hover:bg-n-brand/10'"
        aria-label="Cambiar el ancho de Mi bandeja"
        title="Arrastra para cambiar el ancho"
        @pointerdown.prevent="startInboxResize"
      />
    </aside>

    <main
      class="flex-1 min-w-0 w-full flex flex-col"
      :class="!activeThread ? 'hidden md:flex' : 'flex'"
    >
      <template v-if="activeThread">
        <header
          class="h-16 shrink-0 px-3 md:px-4 flex items-center gap-3 border-b border-n-weak bg-n-solid-1"
        >
          <button
            class="grid size-11 shrink-0 place-items-center rounded-full text-3xl md:hidden"
            aria-label="Volver a Mi bandeja"
            @click="activeThread = null"
          >
            ‹
          </button>
          <div
            class="relative grid size-10 shrink-0 place-items-center overflow-visible rounded-full font-bold"
            :class="
              activeThread.thread_type === 'group_chat'
                ? 'bg-black'
                : 'bg-pink-100 text-pink-700'
            "
          >
            <span
              v-if="activeThread.thread_type === 'group_chat'"
              class="text-lg text-white"
              aria-label="Grupo"
            >👥</span>
            <template v-else>
              {{ activeName.slice(0, 2).toUpperCase() }}
              <img
                :src="brandLogoUrl"
                alt="Mi Marciana"
                class="absolute -bottom-0.5 -right-0.5 size-4 rounded-full border-2 border-white object-cover shadow-sm"
              />
            </template>
          </div>
          <div class="min-w-0">
            <h2 class="truncate text-sm font-semibold">{{ activeName }}</h2>
            <p class="text-xs text-n-slate-10">
              {{ activeThread.members.length }} participantes
            </p>
          </div>
        </header>
        <section
          ref="messageList"
          class="flex-1 min-h-0 touch-pan-y overflow-y-auto overscroll-contain px-3 py-4 md:p-6 space-y-3 bg-[#efeae2] dark:bg-n-alpha-1 [-webkit-overflow-scrolling:touch]"
        >
          <article
            v-for="message in messages"
            :key="message.id"
            class="flex"
            :class="
              message.sender.id === currentUserId
                ? 'justify-end'
                : 'justify-start'
            "
          >
            <div
              class="max-w-[82%] md:max-w-[70%] rounded-2xl px-3 py-2 shadow-sm"
              :class="
                message.kind === 'task'
                  ? 'bg-amber-50 border border-amber-200 text-slate-900'
                  : message.sender.id === currentUserId
                    ? 'bg-n-brand text-white'
                    : 'bg-n-solid-2'
              "
            >
              <div
                v-if="activeThread.thread_type === 'group_chat'"
                class="mb-1 border-b border-current/15 pb-1 text-xs font-semibold opacity-80"
              >
                {{ message.sender.name }}
              </div>
              <div
                v-if="sharedCatalogCard(message)"
                class="w-64 max-w-full overflow-hidden rounded-xl bg-white text-slate-900 shadow-sm"
              >
                <button
                  v-if="sharedCatalogCard(message).image"
                  type="button"
                  class="block w-full cursor-zoom-in bg-slate-50"
                  title="Ampliar imagen sin salir del chat"
                  @click="catalogImagePreview = sharedCatalogCard(message)"
                >
                  <img
                    :src="sharedCatalogCard(message).image"
                    :alt="sharedCatalogCard(message).title"
                    class="h-36 w-full object-contain"
                  />
                </button>
                <div class="p-3">
                  <p class="mb-1 text-[11px] font-bold uppercase tracking-wide text-pink-600">
                    {{ sharedCatalogCard(message).type === 'product' ? 'Producto' : 'Pedido' }}
                  </p>
                  <strong class="block text-sm leading-5">{{ sharedCatalogCard(message).title }}</strong>
                  <template v-if="sharedCatalogCard(message).type === 'product'">
                    <p class="mt-1 text-xs text-slate-600">
                      <span v-if="sharedCatalogCard(message).reference">Ref. {{ sharedCatalogCard(message).reference }} · </span>
                      {{ sharedCatalogCard(message).price || 'Sin precio' }}
                    </p>
                    <p class="mt-1 text-xs font-semibold" :class="sharedCatalogCard(message).stock > 0 ? 'text-emerald-700' : 'text-red-600'">
                      {{ sharedCatalogCard(message).stock > 0 ? `${sharedCatalogCard(message).stock} unidades disponibles` : 'Sin stock' }}
                    </p>
                    <a
                      v-if="sharedCatalogCard(message).adminUrl"
                      :href="sharedCatalogCard(message).adminUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="mt-3 block rounded-lg bg-pink-600 px-3 py-2 text-center text-xs font-bold text-white no-underline"
                    >Abrir en Shopify</a>
                    <button
                      v-else-if="sharedCatalogCard(message).url"
                      type="button"
                      class="mt-3 block w-full rounded-lg border border-slate-200 px-3 py-2 text-center text-xs font-bold text-slate-700"
                      @click="catalogImagePreview = sharedCatalogCard(message)"
                    >Ver producto sin salir del chat</button>
                  </template>
                  <template v-else>
                    <p class="mt-1 text-xs text-slate-600">{{ sharedCatalogCard(message).customer }}</p>
                    <p v-if="sharedCatalogCard(message).phone" class="text-xs text-slate-500">{{ sharedCatalogCard(message).phone }}</p>
                    <p class="mt-2 text-sm font-bold">{{ sharedCatalogCard(message).total }}</p>
                    <p class="mt-1 text-[11px] text-slate-500">
                      {{ sharedCatalogCard(message).financialStatus }} · {{ sharedCatalogCard(message).fulfillmentStatus }}
                    </p>
                  </template>
                  <time class="mt-2 block text-right text-[10px] text-slate-400">{{ formatTime(message.created_at) }}</time>
                </div>
              </div>
              <div v-else class="flex items-end gap-2">
                <div
                  class="min-w-0 flex-1 whitespace-pre-wrap text-[15px] leading-5"
                >
                  {{ messageDisplayBody(message) }}
                </div>
                <time class="shrink-0 text-[10px] leading-4 opacity-65">
                  {{ formatTime(message.created_at) }}
                </time>
              </div>
              <div v-if="message.attachments?.length" class="mt-2 space-y-2">
                <template
                  v-for="attachment in message.attachments"
                  :key="attachment.id"
                >
                  <img
                    v-if="attachment.content_type?.startsWith('image/')"
                    :src="attachment.url"
                    :alt="attachment.filename"
                    class="max-h-72 w-full rounded-xl object-cover"
                  />
                  <audio
                    v-else-if="attachment.content_type?.startsWith('audio/')"
                    :src="attachment.url"
                    controls
                    class="max-w-full"
                  />
                  <a
                    v-else
                    :href="attachment.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block rounded-lg bg-black/10 px-3 py-2 underline"
                  >
                    📎 {{ attachment.filename }}
                  </a>
                </template>
              </div>
              <div
                v-if="commentQuickActionGroups(message).length"
                class="mt-3 space-y-4 border-t border-black/10 pt-3"
              >
                <section
                  v-for="group in commentQuickActionGroups(message)"
                  :key="`${message.id}-${group.title || 'acciones'}`"
                  class="space-y-2"
                >
                  <h3
                    v-if="group.title"
                    class="text-xs font-bold uppercase tracking-wide text-slate-600"
                  >
                    {{ group.title }}
                  </h3>
                  <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      v-for="action in group.actions"
                      :key="`${message.id}-${action.value}`"
                      type="button"
                      class="min-h-11 rounded-xl border px-3 py-2 text-left text-sm font-semibold transition disabled:cursor-wait disabled:opacity-60"
                      :class="{
                        'border-blue-200 bg-blue-50 text-blue-950 hover:bg-blue-100':
                          action.tone === 'primary',
                        'border-pink-200 bg-pink-50 text-pink-950 hover:bg-pink-100':
                          action.tone === 'secondary',
                        'border-slate-300 bg-white text-slate-800 hover:bg-slate-50':
                          action.tone === 'neutral',
                        'border-red-300 bg-red-50 text-red-700 hover:bg-red-100':
                          action.tone === 'danger',
                      }"
                      :disabled="isSending"
                      @click="sendQuickReply(action.value)"
                    >
                      <span v-if="quickReplyValue === String(action.value)"
                        >Procesando…</span
                      >
                      <span v-else>{{ action.label }}</span>
                    </button>
                  </div>
                </section>
              </div>
              <div
                v-if="message.kind === 'task'"
                class="mt-3 pt-3 border-t border-amber-200 text-sm"
              >
                <div class="flex flex-wrap gap-2 items-center">
                  <strong>✓ {{ message.assigned_to?.name }}</strong>
                  <span v-if="message.due_at"
                    >⏰ {{ formatDate(message.due_at) }}</span
                  >
                  <span
                    v-if="message.priority !== 'normal'"
                    class="uppercase text-xs font-bold text-red-600"
                    >{{ message.priority }}</span
                  >
                </div>
                <button
                  v-if="message.conversation_id"
                  class="mt-3 inline-flex items-center gap-2 rounded-lg bg-n-brand px-3 py-2 font-semibold text-white no-underline hover:opacity-90"
                  @click="openLinkedConversation(message.conversation_id)"
                >
                  💬 Abrir conversación
                </button>
                <div class="flex gap-2 mt-3">
                  <button
                    v-if="message.task_status !== 'completed'"
                    class="px-3 py-1.5 rounded-lg border border-emerald-600 bg-white font-semibold text-emerald-700 hover:bg-emerald-50"
                    @click="updateTask(message, 'completed')"
                  >
                    Completar
                  </button>
                  <button
                    v-if="message.task_status === 'pending'"
                    class="px-3 py-1.5 rounded-lg bg-white border"
                    @click="updateTask(message, 'in_progress')"
                  >
                    Empezar
                  </button>
                  <details
                    v-if="message.task_status !== 'completed'"
                    class="relative"
                  >
                    <summary
                      class="cursor-pointer list-none rounded-lg border bg-white px-3 py-1.5 font-semibold"
                    >
                      Seguir pendiente
                    </summary>
                    <div class="absolute bottom-full left-0 z-20 mb-1 grid min-w-40 gap-1 rounded-xl border bg-white p-2 shadow-xl">
                      <button class="rounded-lg px-3 py-2 text-left hover:bg-slate-100" @click="postponeTask(message, 1)">Mañana</button>
                      <button class="rounded-lg px-3 py-2 text-left hover:bg-slate-100" @click="postponeTask(message, 2)">En 2 días</button>
                      <button class="rounded-lg px-3 py-2 text-left hover:bg-slate-100" @click="postponeTask(message, 3)">En 3 días</button>
                    </div>
                  </details>
                  <span
                    v-if="message.task_status === 'completed'"
                    class="text-emerald-700 font-semibold"
                    >Completada</span
                  >
                </div>
              </div>
            </div>
          </article>
        </section>
        <footer
          class="relative shrink-0 border-t border-n-weak bg-n-solid-1 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:p-4"
        >
          <p
            v-if="sendError"
            class="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {{ sendError }}
          </p>
          <div
            v-if="showComposerMenu"
            class="absolute bottom-[calc(100%+0.25rem)] left-2 z-20 min-w-52 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-1 shadow-xl"
          >
            <button
              class="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-n-alpha-1"
              @click="
                fileInput?.click();
                showComposerMenu = false;
              "
            >
              <span
                class="grid size-9 place-items-center rounded-full bg-violet-100 text-lg"
                >🖼️</span
              >
              Foto o cámara
            </button>
            <button
              class="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-n-alpha-1"
              @click="
                documentInput?.click();
                showComposerMenu = false;
              "
            >
              <span
                class="grid size-9 place-items-center rounded-full bg-slate-100 text-lg"
                >📎</span
              >
              Archivo
            </button>
            <button
              class="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-n-alpha-1"
              @click="openCatalogSearch('product')"
            >
              <span
                class="grid size-9 place-items-center rounded-full bg-pink-100 text-lg"
                >🛍️</span
              >
              Buscar producto
            </button>
            <button
              class="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-n-alpha-1"
              @click="openCatalogSearch('order')"
            >
              <span
                class="grid size-9 place-items-center rounded-full bg-blue-100 text-lg"
                >📦</span
              >
              Buscar pedido
            </button>
            <button
              class="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-n-alpha-1"
              @click="selectTask"
            >
              <span
                class="grid size-9 place-items-center rounded-full bg-amber-100 text-lg"
                >✓</span
              >
              Crear tarea
            </button>
          </div>
          <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            accept="image/*,video/*"
            @change="onFilesSelected"
          />
          <input
            ref="documentInput"
            type="file"
            multiple
            class="hidden"
            accept="audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            @change="onFilesSelected"
          />
          <div
            v-if="composeType === 'task'"
            class="relative grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-slate-900"
          >
            <button
              class="absolute right-2 top-1 text-xl text-slate-500"
              title="Cerrar tarea"
              @click="composeType = 'message'"
            >
              ×
            </button>
            <select
              v-model="assignedToId"
              class="rounded-xl border border-amber-200 bg-white p-2 pr-8"
            >
              <option value="">Asignar a…</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
            <div class="sm:col-span-3">
              <button
                type="button"
                class="flex w-full items-center justify-between rounded-xl border border-amber-200 bg-white px-3 py-2 text-left"
                @click="showConversationPicker = !showConversationPicker"
              >
                <span>
                  {{
                    linkedConversationId
                      ? `🔗 Conversación #${linkedConversationId}`
                      : 'Vincular una conversación (opcional)'
                  }}
                </span>
                <span>⌄</span>
              </button>
              <div
                v-if="showConversationPicker"
                class="mt-2 rounded-2xl border border-amber-200 bg-white p-2 shadow-lg"
              >
                <div class="relative mb-2">
                  <span
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >⌕</span
                  >
                  <input
                    v-model="conversationSearch"
                    type="search"
                    class="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 outline-none focus:border-amber-400"
                    placeholder="Buscar por nombre, usuario o número…"
                  />
                </div>
                <div
                  class="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2"
                >
                  <button
                    type="button"
                    class="flex min-h-16 items-center gap-3 rounded-xl border border-dashed border-slate-300 p-3 text-left text-slate-600"
                    @click="
                      linkedConversationId = '';
                      showConversationPicker = false;
                    "
                  >
                    <span
                      class="grid size-10 shrink-0 place-items-center rounded-full bg-slate-100"
                      >×</span
                    >
                    <span>Sin conversación vinculada</span>
                  </button>
                  <button
                    v-for="conversation in filteredConversations"
                    :key="conversation.id"
                    type="button"
                    class="flex min-h-16 items-center gap-3 rounded-xl border p-2.5 text-left transition hover:bg-amber-50"
                    :class="
                      String(linkedConversationId) === String(conversation.id)
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-n-weak'
                    "
                    @click="
                      linkedConversationId = conversation.id;
                      showConversationPicker = false;
                    "
                  >
                    <div class="relative size-11 shrink-0">
                      <img
                        v-if="conversationAvatar(conversation)"
                        :src="conversationAvatar(conversation)"
                        alt=""
                        class="size-11 rounded-full object-cover"
                      />
                      <span
                        v-else
                        class="grid size-11 place-items-center rounded-full bg-pink-100 font-bold text-pink-700"
                        >{{
                          conversationLabel(conversation)
                            .slice(0, 2)
                            .toUpperCase()
                        }}</span
                      >
                      <span
                        class="absolute -bottom-1 -right-1 grid size-5 place-items-center rounded-full border-2 border-white bg-white text-xs"
                        >{{ conversationChannel(conversation).icon }}</span
                      >
                    </div>
                    <span class="min-w-0 flex-1">
                      <strong class="block truncate">{{
                        conversationLabel(conversation)
                      }}</strong>
                      <small class="block truncate text-slate-500">
                        {{
                          conversationUsername(conversation) ||
                          conversationChannel(conversation).label
                        }}
                        · #{{ conversation.id }}
                      </small>
                    </span>
                  </button>
                  <p
                    v-if="!filteredConversations.length"
                    class="col-span-full p-5 text-center text-sm text-slate-500"
                  >
                    No se encontraron conversaciones.
                  </p>
                </div>
              </div>
            </div>
            <input
              v-model="dueAt"
              type="datetime-local"
              class="rounded-xl border border-amber-200 bg-white p-2"
            />
            <select
              v-model="priority"
              class="rounded-xl border border-amber-200 bg-white p-2"
            >
              <option value="normal">Prioridad normal</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div class="flex h-12 items-center gap-1.5">
            <button
              class="grid size-11 shrink-0 place-items-center rounded-full text-3xl font-light leading-none text-n-slate-11"
              title="Añadir"
              @click="showComposerMenu = !showComposerMenu"
            >
              +
            </button>
            <div
              class="flex h-11 min-w-0 flex-1 items-center rounded-[22px] border border-n-weak bg-n-solid-2 px-3 focus-within:border-n-brand"
            >
              <textarea
                v-model="body"
                rows="1"
                class="h-8 min-h-8 w-full appearance-none resize-none overflow-y-auto rounded-none border-0 bg-transparent py-1 text-[15px] leading-6 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none"
                :placeholder="
                  composeType === 'task' ? 'Describe la tarea…' : 'Mensaje'
                "
                @keydown.ctrl.enter.prevent="send([])"
                @focus="showComposerMenu = false"
              />
            </div>
            <button
              v-if="!body.trim() && composeType !== 'task'"
              class="grid size-10 shrink-0 place-items-center rounded-full text-xl text-n-slate-12"
              title="Enviar foto"
              @click="fileInput?.click()"
            >
              <svg
                viewBox="0 0 24 24"
                class="size-6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14.5 4 16 7h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3l1.5-3z"
                />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
            </button>
            <button
              v-if="body.trim() || composeType === 'task'"
              class="grid size-11 shrink-0 place-items-center rounded-full bg-[#111827] text-white shadow-sm disabled:opacity-40"
              :disabled="
                isSending ||
                !body.trim() ||
                (composeType === 'task' && !assignedToId)
              "
              aria-label="Enviar mensaje"
              @click="send([])"
            >
              <svg
                v-if="!isSending"
                viewBox="0 0 24 24"
                class="size-5 translate-x-px"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M3 20.5 22 12 3 3.5v6.6l13 1.9-13 1.9z" />
              </svg>
              <span
                v-else
                class="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              />
              <span class="sr-only">Enviar mensaje</span>
            </button>
            <button
              v-else
              class="grid size-10 shrink-0 place-items-center rounded-full text-xl"
              :class="
                isRecording
                  ? 'bg-red-600 text-white animate-pulse'
                  : 'text-n-slate-12'
              "
              :title="isRecording ? 'Detener y enviar audio' : 'Grabar audio'"
              @click="toggleRecording"
            >
              <span v-if="isRecording">■</span>
              <svg
                v-else
                viewBox="0 0 24 24"
                class="size-6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0M12 17v5M8 22h8" />
              </svg>
            </button>
          </div>
        </footer>
      </template>
      <div
        v-else
        class="hidden md:grid place-items-center h-full text-n-slate-10"
      >
        <div class="text-center">
          <div class="text-6xl mb-4">🪐</div>
          <h2 class="text-xl font-semibold">Tu espacio de trabajo interno</h2>
          <p>Selecciona un chat o crea uno nuevo.</p>
        </div>
      </div>
    </main>

    <div
      v-if="catalogImagePreview"
      class="fixed inset-0 z-[90] flex flex-col bg-black/90 p-3 text-white"
      @click.self="catalogImagePreview = null"
    >
      <header class="flex shrink-0 items-center justify-between gap-3 pb-3">
        <strong class="min-w-0 truncate text-sm">
          {{ catalogImagePreview.title }}
        </strong>
        <button
          type="button"
          class="grid size-11 shrink-0 place-items-center rounded-full bg-white text-2xl text-slate-900 shadow-lg"
          aria-label="Cerrar imagen y volver al chat"
          @click="catalogImagePreview = null"
        >×</button>
      </header>
      <button
        type="button"
        class="min-h-0 flex-1 cursor-zoom-out"
        aria-label="Cerrar imagen y volver al chat"
        @click="catalogImagePreview = null"
      >
        <img
          :src="catalogImagePreview.image"
          :alt="catalogImagePreview.title"
          class="size-full object-contain"
        />
      </button>
      <div
        v-if="catalogImagePreview.type === 'product'"
        class="mx-auto w-full max-w-lg shrink-0 rounded-2xl bg-white p-4 text-slate-900"
      >
        <strong class="block text-base">{{ catalogImagePreview.title }}</strong>
        <p class="mt-1 text-sm text-slate-600">
          <span v-if="catalogImagePreview.reference">Ref. {{ catalogImagePreview.reference }} · </span>
          {{ catalogImagePreview.price || 'Sin precio' }}
        </p>
        <p
          class="mt-1 text-sm font-semibold"
          :class="catalogImagePreview.stock > 0 ? 'text-emerald-700' : 'text-red-600'"
        >
          {{ catalogImagePreview.stock > 0 ? `${catalogImagePreview.stock} unidades disponibles` : 'Sin stock' }}
        </p>
      </div>
      <p class="shrink-0 pt-3 text-center text-xs text-white/70">
        Pulsa la × o la imagen para volver al chat
      </p>
    </div>

    <div
      v-if="showCatalogSearch"
      class="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-4"
      @click.self="closeCatalogSearch"
    >
      <section class="flex max-h-[82dvh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white text-slate-900 shadow-2xl">
        <header class="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 class="text-xl font-bold">
              {{ catalogSearchType === 'product' ? 'Buscar producto' : 'Buscar pedido' }}
            </h2>
            <p class="text-xs text-slate-500">Se enviará al chat sin vincular ni modificar datos.</p>
          </div>
          <button class="grid size-10 place-items-center rounded-full bg-slate-100 text-xl" @click="closeCatalogSearch">×</button>
        </header>
        <form class="p-4" @submit.prevent="searchCatalog">
          <input
            v-model="catalogQuery"
            autofocus
            type="search"
            class="min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-base outline-none focus:border-pink-500"
            :placeholder="catalogSearchType === 'product' ? 'Nombre, referencia o SKU' : 'Número de pedido'"
          />
        </form>
        <p v-if="catalogError" class="mx-4 mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{{ catalogError }}</p>
        <div class="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-5">
          <article
            v-for="item in catalogResults"
            :key="item.id"
            class="flex gap-3 rounded-2xl border border-slate-200 p-3"
          >
            <img
              v-if="catalogSearchType === 'product' && (item.image || item.images?.[0]?.url)"
              :src="item.image || item.images?.[0]?.url"
              :alt="item.name || item.title"
              class="size-20 shrink-0 rounded-xl bg-slate-50 object-contain"
            />
            <div class="min-w-0 flex-1">
              <strong class="block text-sm leading-5">
                {{ catalogSearchType === 'product' ? (item.name || item.title) : `Pedido ${item.name}` }}
              </strong>
              <template v-if="catalogSearchType === 'product'">
                <p class="mt-1 text-xs text-slate-500">{{ item.ref || item.sku || 'Sin referencia' }} · {{ item.price?.formatted || 'Sin precio' }}</p>
                <p class="mt-1 text-xs font-semibold" :class="Number(item.totalStock || 0) > 0 ? 'text-emerald-700' : 'text-red-600'">
                  {{ Number(item.totalStock || 0) > 0 ? `${item.totalStock} disponibles` : 'Sin stock' }}
                </p>
              </template>
              <template v-else>
                <p class="mt-1 text-xs text-slate-600">{{ item.customer?.name || 'Sin clienta' }}</p>
                <p class="text-xs text-slate-500">{{ item.total?.formatted || '' }} · {{ item.displayFinancialStatus }} · {{ item.displayFulfillmentStatus }}</p>
              </template>
              <button
                class="mt-3 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                :disabled="isSending"
                @click="sendCatalogCard(item)"
              >
                {{ isSending ? 'Enviando…' : 'Enviar al chat' }}
              </button>
            </div>
          </article>
          <p v-if="!catalogResults.length && !catalogLoading && !catalogError" class="py-8 text-center text-sm text-slate-400">
            {{ catalogSearchType === 'product' ? 'Busca una prenda para compartirla.' : 'Escribe el número exacto del pedido.' }}
          </p>
        </div>
      </section>
    </div>

    <div
      v-if="showNewTask"
      class="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4"
      @click.self="showNewTask = false"
    >
      <div class="flex max-h-[calc(100dvh-1rem)] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white p-5 text-slate-900 shadow-2xl md:max-h-[90dvh]">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-xl font-bold">Crear tarea</h2>
            <p class="text-sm text-slate-500">Deja algo pendiente en unos segundos.</p>
          </div>
          <button class="grid size-9 place-items-center rounded-full bg-slate-100 text-xl" @click="showNewTask = false">×</button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto pr-1">
        <label class="mb-4 block text-sm font-semibold">
          ¿Qué hay que hacer?
          <textarea
            v-model="newTaskBody"
            rows="3"
            autofocus
            class="mt-1 w-full resize-none rounded-2xl border border-slate-200 p-3 font-normal outline-none focus:border-amber-500"
            placeholder="Ej.: Comprobar si se ha entregado el pedido"
          />
        </label>
        <label class="mb-4 block text-sm font-semibold">
          Responsable
          <select v-model="newTaskResponsibleId" class="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 font-normal">
            <option value="">Elegir responsable…</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.id === currentUserId ? `${agent.name} (para mí)` : agent.name }}
            </option>
          </select>
        </label>
        <fieldset class="mb-4">
          <legend class="mb-1 text-sm font-semibold">También participa (opcional)</legend>
          <div class="grid grid-cols-2 gap-2 rounded-2xl bg-slate-50 p-2">
            <label v-for="agent in agents.filter(agent => String(agent.id) !== String(newTaskResponsibleId))" :key="agent.id" class="flex items-center gap-2 rounded-xl p-2 text-sm hover:bg-white">
              <input v-model="newTaskParticipantIds" type="checkbox" :value="agent.id" />
              <span>{{ agent.name }}</span>
            </label>
          </div>
        </fieldset>
        <div class="mb-2 text-sm font-semibold">¿Cuándo?</div>
        <div class="mb-3 flex flex-wrap gap-2">
          <button type="button" class="rounded-full bg-slate-100 px-3 py-2 text-sm" @click="dueShortcut(0)">Hoy</button>
          <button type="button" class="rounded-full bg-slate-100 px-3 py-2 text-sm" @click="dueShortcut(1)">Mañana</button>
          <button type="button" class="rounded-full bg-slate-100 px-3 py-2 text-sm" @click="newTaskDueAt = ''">Sin fecha</button>
        </div>
        <input v-model="newTaskDueAt" type="datetime-local" class="mb-4 w-full rounded-xl border border-slate-200 p-3" />
        <label class="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
          <input v-model="newTaskFollowUp" type="checkbox" class="mt-1" />
          <span><strong class="block">Seguimiento</strong><small>Se mantendrá controlada hasta que alguien la complete.</small></span>
        </label>
        <p v-if="linkedConversationId" class="mb-4 rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-900">🔗 Quedará vinculada a la conversación #{{ linkedConversationId }}</p>
        </div>
        <button
          class="mt-3 w-full shrink-0 rounded-2xl !bg-black p-3.5 font-bold !text-white disabled:opacity-40"
          style="background-color: #111827 !important; color: #ffffff !important"
          :disabled="creatingTask || !newTaskBody.trim() || !newTaskResponsibleId"
          @click="createStandaloneTask"
        >
          {{ creatingTask ? 'Creando…' : 'Crear tarea' }}
        </button>
      </div>
    </div>

    <div
      v-if="showNewThread"
      class="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      @click.self="showNewThread = false"
    >
      <div class="w-full max-w-md rounded-2xl bg-n-solid-1 p-5 shadow-xl">
        <h2 class="text-xl font-bold mb-1">Nuevo chat interno</h2>
        <p class="text-sm text-n-slate-10 mb-4">
          Selecciona una persona o varias para crear un grupo.
        </p>
        <input
          v-model="newThreadName"
          class="w-full rounded-xl border border-n-weak bg-n-solid-2 p-3 mb-3"
          placeholder="Nombre del grupo (opcional)"
        />
        <div class="max-h-64 overflow-y-auto space-y-2">
          <label
            v-for="agent in agents.filter(a => a.id !== currentUserId)"
            :key="agent.id"
            class="flex items-center gap-3 p-3 rounded-xl hover:bg-n-alpha-1"
            ><input
              v-model="newMemberIds"
              type="checkbox"
              :value="agent.id"
            /><span>{{ agent.name }}</span></label
          >
        </div>
        <div class="flex justify-end gap-2 mt-5">
          <button class="px-4 py-2" @click="showNewThread = false">
            Cancelar</button
          ><button
            class="px-4 py-2 rounded-xl bg-n-brand text-white disabled:opacity-40"
            :disabled="!newMemberIds.length"
            @click="createThread"
          >
            Crear
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
